/**
 * ── A PORTA DO TUTOR, FLUTUANTE NO CANTO ───────────────────────────────────
 *
 * Escolha do dono em 30/09/2026, posta ao lado das alternativas medidas e feita
 * sabendo o que ela contraria: `docs/aluno-assistente-desenho.md:695` (§8.1)
 * recusa por escrito «um balão redondo flutuante no canto inferior direito». A
 * ordem do dono venceu o §8.1; as razões dele continuam verdadeiras, e é por isso
 * que este ficheiro tem as guardas que tem — e por isso que a porta **não é um
 * balão**.
 *
 * ── O DEFEITO DE 01/10/2026, E A RAZÃO DO PORTAL ───────────────────────────
 *
 * Primeira versão no ar: o botão pousou no canto inferior direito **do
 * cabeçalho**, por cima do link «Áreas», e abrir o painel não mostrava nada.
 *
 * Uma causa só, e ela não é de `z-index`: **`backdrop-filter` cria bloco de
 * contenção para `position: fixed`**. O `<header>` tem `backdrop-blur`, e esta
 * porta montava dentro dele — logo `bottom: 1rem` passou a ser «1rem acima do
 * fundo do CABEÇALHO», e o painel `inset-x-0 bottom-0` virou uma tira da altura
 * do cabeçalho, escondida por baixo dele.
 *
 * Medido num par mínimo, com a mesma classe e nada mais a mudar entre os dois:
 *
 * ```
 *                        com backdrop-filter      sem
 *  porta  bottom:16px    topo  -8 (o cabeçalho)   topo 1221 (a janela) ✓
 *  painel inset:0        2541 × 43                2541 × 1272          ✓
 * ```
 *
 * `createPortal` é o conserto certo e não um remendo: a porta **é** da janela, não
 * do cabeçalho. Ela continua a nascer aqui porque é o cabeçalho que tem o
 * `areaSlug` e o interruptor na mão, e é o único componente que monta em toda tela
 * da área — o que muda é só onde ela pinta.
 *
 * ── E O ALVO DO PORTAL NÃO É O `<body>` (01/10/2026, segunda volta) ───────
 *
 * A primeira correção portou para `document.body`, e o dono viu o resultado: o
 * painel abriu **sem tema nenhum** — a conversa por cima da fotografia da vitrine,
 * ilegível.
 *
 * As variáveis do tema vivem no `<div style={variaveisDoTema(...)}>` de CADA rota
 * (`App.tsx:887` e irmãs). Nunca no `:root`, nunca no `<body>`. Custom properties
 * herdam pela árvore do DOM, e o `<body>` está FORA dessa árvore: lá, `--fundo`,
 * `--texto`, `--superficie` e `--destaque` simplesmente não existem, e
 * `background: var(--superficie)` sem valor é declaração inválida — isto é,
 * transparente.
 *
 * A casa já tinha escrito isto, e eu li o ficheiro sem ler o parágrafo:
 * `busca/bancada-da-busca.tsx` diz *«montado fora, `--fundo`, `--texto` e
 * `--destaque` não existem — e a primeira captura desta obra saiu inteiramente
 * preta por causa disso»*.
 *
 * O alvo certo satisfaz as DUAS condições ao mesmo tempo, e há um só elemento que
 * as satisfaz: **o pai do cabeçalho**.
 *
 * - está DENTRO do bloco do tema, logo herda as variáveis (e herda mesmo que
 *   alguém um dia embrulhe o cabeçalho: custom property herda por toda a cadeia);
 * - está FORA do `<header>`, que é quem tem o `backdrop-filter`.
 *
 * O `document.body` fica como último recurso, e nunca devia ser alcançado — se for,
 * a porta aparece sem tema, e é a cerca que diz isso antes de o dono dizer.
 *
 * ## O que sobrou do §8.1, e não se negocia
 *
 * **Esta porta não existe em `/aula/:id`.** Ali já há duas: o trilho de dois
 * estados em desktop e a alça colada ao rodapé no celular. Duas barras fixas no
 * mesmo rodapé é exactamente o defeito que `FolhaDaDoca` existe para não cometer.
 *
 * ## O que ela paga no pedaço de entrada, e como se mantém pequena
 *
 * O teto é `index-*.js ≤ 335.000 B`. Esta porta **não conhece a loja da Doca**:
 * `doca/estado.ts` custa 3.122 B, medido e escrito no cabeçalho dele, e importá-lo
 * daqui gastaria a folga num módulo que só serve dentro do painel. O que ela
 * guarda é um booleano local — *pedi para abrir* —, e quem fala com a loja é o
 * painel, que chega por `lazy()` no mesmo gesto.
 *
 * A consequência assumida: **este botão não sabe se há conversa em curso**, e por
 * isso não diz «voltar à resposta» como a alça diz. Um rótulo só, sempre o mesmo.
 */
import { Suspense, lazy, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { NOME_DA_DOCA } from './alcance.js';
import { useT } from '../lib/idioma.js';
import { Capelo } from './Capelo.js';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';

/*
 * O painel — e com ele a loja, o contrato, a marcação e a prosa — chega quando
 * alguém abre. É a mesma decisão do `PainelDeBusca` em `Cabecalho.tsx`: o pedaço
 * de entrada paga o botão, e nada mais.
 */
const PainelDaCasca = telaComDicionario(() =>
  import('./PainelDaCasca.js').then((m) => ({ default: m.PainelDaCasca })),
);

/** Uma aula abre-se em `…/aula/:id` nos dois modos de endereço (host e painel). */
function naSalaDeAula(caminho: string): boolean {
  return caminho.includes('/aula/');
}

/**
 * ── O FIO, QUE É O GLIFO DESTA CASA ────────────────────────────────────────
 *
 * Três nós num fio, o do meio maior.
 *
 * **Não é um balão de fala** — isso é atendimento, e foi a primeira razão do
 * §8.1. **Não é uma faísca** — é com ela que meio mercado assina IA, e seria o
 * contrário de «sofisticado»: é o glifo mais previsível que existe hoje. **Não é
 * um robô nem um rosto** — mascote dentro da marca de outra pessoa não é nosso.
 *
 * É o **fio**: o mesmo device de 1 px que a trilha usa para ligar as etapas, e o
 * mesmo que a linha de citação desenha ao levar o aluno da resposta à aula. Ele
 * diz a coisa que este agente faz de verdade — **ligar o que está separado**: a
 * dúvida à aula, a aula ao módulo, o módulo ao caminho. Um glifo que nomeia o
 * trabalho, em vez de nomear a tecnologia.
 *
 * O nó do meio é maior porque é «onde você está» — a mesma gramática do trajeto.
 *
 * ## O que a bancada decidiu, e o que ela recusou (01/10/2026)
 *
 * Três geometrias desenhadas lado a lado, com o CSS construído, sobre fotografia:
 *
 * - **ziguezague** (sobe e desce): lê-se como gráfico de dispersão, e o joelho no
 *   meio é um acidente que o olho tenta interpretar;
 * - **diagonal a DESCER**: lê-se como queda. Num produto de estudo, uma linha que
 *   desce diz a coisa errada sem dizer nada;
 * - **diagonal a SUBIR** (esta): lê-se como caminho percorrido, com «onde você
 *   está» no meio. É a única das três que descreve o trabalho do agente.
 *
 * Recusado na mesma passada: um **halo** em volta do nó do meio. A ideia era
 * reforçar o «você está aqui»; a 22 px o anel vira borrão em torno do nó e tira a
 * legibilidade que ele devia dar. Antes de sair de casa, tira-se um acessório.
 *
 * A primeira versão tinha 19 px e nós de 1,7: **lia-se como rabisco**. O tamanho
 * foi medido, não escolhido — a 22 px com nós de 2,3/3,4 o glifo resolve, e
 * continua a resolver aos 48×48 do celular, onde ele fica sozinho.
 */
/**
 * ── A ÓRBITA: O SINAL DE QUE HÁ ALGUÉM ────────────────────────────────────
 *
 * Ordem do dono, 01/10/2026: *«deve ter ícone de agente, cara de agente, não um
 * botão perguntar; algo premium que simbolize um agente»*.
 *
 * Um arco curto e um ponto, correndo por fora do orbe. Cinco marcas foram
 * desenhadas e medidas lado a lado nos dois temas — selo, pérola, anel, órbita,
 * selo+órbita — e esta é a que o dono escolheu: é a única que diz **«há algo aqui
 * em movimento»**, que é o que separa um agente de um botão.
 *
 * O **anel** caiu por medição e não por gosto: miolo translúcido, a página
 * atravessa — o mesmo defeito que o dono acabara de reportar. O **selo** e o
 * **selo+órbita** caíram por volume: sobre um herói com fotografia, o disco cheio
 * é o objeto mais alto da tela em toda rota.
 *
 * O que continua proibido é o §8.8: nada de robô, de rosto, de faísca (o glifo
 * com que meio mercado assina IA) nem de mascote dentro da marca de outra pessoa.
 * Órbita é geometria, e a geometria é nossa.
 *
 * `strokeDasharray` com um traço curto e um vão enorme dá o ARCO sem calcular
 * ângulo nenhum: 26 de traço num perímetro de ~195 é um sexto da volta.
 *
 * ── E O ARCO NASCE À ESQUERDA, QUE É O ÚNICO LADO QUE NÃO CRESCE ───────────
 *
 * Medido na bancada: com o arco no começo natural do `<circle>` — as 3 horas —
 * ele parava exactamente onde a palavra entra. A pílula cresce PARA A DIREITA, e
 * a órbita é presa ao orbe, então o arco em repouso atravessava «Perguntar» como
 * um risco. Não era desenho: era colisão.
 *
 * `strokeDashoffset` roda o traço sem rodar o desenho: −97 de um perímetro de
 * ~195 é meia volta, e põe o arco a correr das 9 horas para cima — o lado que
 * continua vazio nos dois estados do botão. O ponto vai **à frente** do traço, e
 * não atrás: é a cabeça do cometa, e o arco é o rastro que ela deixou.
 */
function Orbita() {
  return (
    <svg aria-hidden viewBox="0 0 66 66" fill="none" className="vst-porta-orbita">
      <circle
        className="vst-porta-orbita-arco"
        cx="33"
        cy="33"
        r="31"
        stroke="currentColor"
        strokeWidth={1.1}
        strokeDasharray="26 140"
        strokeDashoffset={-97}
        strokeLinecap="round"
      />
      {/* 234° na volta, seis graus à frente da ponta do rastro. */}
      <circle cx="14.8" cy="7.9" r="2.1" fill="currentColor" />
    </svg>
  );
}

function Fio() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      className="vst-porta-fio"
    >
      <path
        d="M4.5 17 12 12l7.5-5"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="vst-porta-fio-linha"
      />
      <circle
        className="vst-porta-no vst-porta-no-1"
        cx="4.5"
        cy="17"
        r="2.3"
        fill="currentColor"
      />
      <circle className="vst-porta-no vst-porta-no-2" cx="12" cy="12" r="3.4" fill="currentColor" />
      <circle
        className="vst-porta-no vst-porta-no-3"
        cx="19.5"
        cy="7"
        r="2.3"
        fill="currentColor"
      />
    </svg>
  );
}

export function PortaFlutuante({
  areaSlug,
  assistente,
}: {
  /** O slug da área. Sem área não há a quem perguntar — nem tecla, nem botão. */
  areaSlug: string;
  /**
   * O interruptor do TUTOR nesta área (`cfg.ia.assistente === 'sim'`).
   *
   * **Ausente lê-se como desligado**, e é a mesma disciplina da sala: a prévia do
   * Estúdio e a bancada não o mandam, e ali a porta não pode existir. Com ele
   * falso não se desenha nada — nem apagado, nem com cadeado, nem com frase
   * (§5.5a): o servidor responde 404 para não confirmar que há um assistente por
   * trás, e uma porta visível desmentiria essa decisão.
   */
  assistente?: boolean | undefined;
}) {
  const t = useT();
  const [aberto, setAberto] = useState(false);
  const { pathname } = useLocation();
  /*
   * O alvo resolve-se DEPOIS da montagem, e não no corpo do render: no primeiro
   * render o `<header>` ainda não está no DOM (o React desenha os filhos antes de
   * os cometer), e a consulta devolveria nulo. Um quadro sem a porta é invisível;
   * uma porta sem tema não é.
   */
  const [alvo, setAlvo] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const cabecalho = document.querySelector('[data-vst-cabecalho]');
    setAlvo((cabecalho?.parentElement as HTMLElement | null) ?? document.body);
  }, []);

  if (assistente !== true) return null;
  if (naSalaDeAula(pathname)) return null;
  /* Sem alvo não há porta: é o primeiro quadro, ou um ambiente sem DOM. Os dois
     casos são silêncio, e nenhum deles pode explodir. */
  if (!alvo) return null;

  return createPortal(
    <>
      {!aberto && (
        <button
          type="button"
          data-vst-porta-do-tutor=""
          onClick={() => setAberto(true)}
          /*
           * O rótulo acessível continua a dizer o GESTO, e não só o nome: quem
           * chega por leitor de tela precisa de saber o que o botão faz, e
           * «Neura Tutora» sozinho não o diz. O nome entra a seguir, porque é
           * assim que uma pessoa se apresenta.
           */
          aria-label={t('assistente.perguntarANeura', { nome: NOME_DA_DOCA })}
          className="vst-porta-do-tutor"
        >
          <Orbita />
          <Fio />
          {/*
           * A palavra SAI do repouso (o dono pediu um agente, não um botão
           * rotulado) e entra sob o rato e sob o foco — mas continua SEMPRE no
           * DOM. É ela que o leitor de tela anuncia, e é ela que aparece para quem
           * chega pelo teclado: não há gesto que alcance esta porta sem poder ler
           * o que ela faz.
           */}
          <span className="vst-porta-do-tutor-palavra">
            <Capelo />
            {NOME_DA_DOCA}
          </span>
        </button>
      )}
      {aberto && (
        /* `fallback={null}`: entre o clique e o pedaço há um quadro, e um painel
           esqueleto nesse quadro pisca mais do que informa — a mesma decisão do
           `PainelDeBusca`. */
        <Suspense fallback={null}>
          <PainelDaCasca areaSlug={areaSlug} aoFechar={() => setAberto(false)} />
        </Suspense>
      )}
    </>,
    alvo,
  );
}
